import { isDefined } from '@sgme/fp';
import { ServiceLoader } from '@sgme/ui';
import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import type { Client, GetCurrentUserResponse, ProductType } from '@/api/user/getCurrentUser.ts';
import { useCurrentUserQuery } from '@/api/user/index.ts';
import { UnsatisfiedPrerequisitesModal } from '@/components/fatality/UnsatisfiedPrerequisitesModal.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectWorkspaceCanTrade } from '@/store/workspace/slice.ts';

export type TradePermission =
  | 'disallowedByUserPermissions' // /api/user/current says 'canTrade: false'
  | 'disallowedByWorkspace' // the workspace is locked by the user
  // in other cases :
  | 'allowed';

export type UserContextValue = {
  currentUser: GetCurrentUserResponse;
  defaultClient: Client;
  getCompanyName: (companyId: number) => string | undefined;
  hasPermissionToTradeFor: (companyId: number) => boolean;
  // tradePermission : this is the source of truth for any tile wanting to execute a deal.
  // it merges the user level clearance for trading and the workspace level lock state
  tradePermission: TradePermission;
};

// exported for tests, or when we are in a context where user information is not guaranteed to have been loaded yet
// in any other case, useUserContext should be used
export const userContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: PropsWithChildren) {
  const { data, isLoading: currentUserIsLoading, isError: currentUserLoadingFailed } = useCurrentUserQuery();
  const workspaceCanTrade = useAppSelector(selectWorkspaceCanTrade);

  const userData: GetCurrentUserResponse | undefined = data && {
    ...data,
    productTypes: (data.productTypes ?? []).reduce<ProductType[]>((acc, type) => {
      acc.push(type === ('AmericanForward' as ProductType) ? 'FlexiForward' : type);
      return acc;
    }, []),
  };

  const value: UserContextValue | null = useMemo(() => {
    const clients = userData?.clients ?? [];
    const [defaultClient] = clients;
    const clientsNameResolutionMap = new Map<number, string>(
      clients.map((client) => [client.companyId, client.companyName]),
    );
    return isDefined(userData) && isDefined(defaultClient)
      ? {
          currentUser: userData,
          defaultClient,
          getCompanyName: (companyId: number | string) =>
            typeof companyId === 'string'
              ? clientsNameResolutionMap.get(Number.parseInt(companyId, 10)) // can't parse companyId to number => Nan => return undefined
              : clientsNameResolutionMap.get(companyId),
          hasPermissionToTradeFor: (companyId: number) => clients.some((client) => client.companyId === companyId),
          tradePermission:
            userData.canTrade !== true // forgive this extra 'paranoïa', we only accept explicit, expectedly well-formed clearance here
              ? 'disallowedByUserPermissions'
              : workspaceCanTrade
                ? 'allowed'
                : 'disallowedByWorkspace',
        }
      : null;
  }, [userData, workspaceCanTrade]);

  if (currentUserIsLoading) {
    // the component accept isAppLoading(bool) props and children to render when value is false
    // but we dont need this component in the tree anymore once the user data is loaded
    return <ServiceLoader />;
  }

  if (currentUserLoadingFailed) {
    return <UnsatisfiedPrerequisitesModal reason="userInfoFetchFailed" />;
  }

  const { clients = [], productTypes = [] } = userData ?? {};

  if (clients.length === 0) {
    return <UnsatisfiedPrerequisitesModal reason="noClient" />;
  }

  if (productTypes.length === 0) {
    return <UnsatisfiedPrerequisitesModal reason="noProduct" />;
  }

  return <userContext.Provider value={value}>{children}</userContext.Provider>;
}

export function useUserContext(): UserContextValue {
  const currentUser = useContext(userContext);

  if (currentUser === null) {
    throw new Error("Can't use useUserContext hook outside of a UserProvider");
  }

  return currentUser;
}

export function checkIfUserInternalSales(userContextValue: UserContextValue): boolean {
  return userContextValue.currentUser.userType === 'Internal Sales';
}

export function checkIfUserCanTrade(userContextValue: UserContextValue): boolean {
  return userContextValue.tradePermission === 'allowed';
}
